import { Head } from "@inertiajs/react"
import { Clock } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading, HeroIcon } from "@/components/tuw/auth/AuthHeading"
import AuthLayout from "@/components/tuw/auth/AuthLayout"
import { CsrfField, PasswordField } from "@/components/tuw/auth/fields"
import { Button } from "@/components/ui/button"

import { BackToSignIn } from "./ForgotPassword"

const BUTTON_CLASS = "h-auto w-full rounded-[11px] px-4 py-3 text-[14px] font-semibold"

// Choose a new password (P11-H2 Spare Key), reached from the emailed link.
// `expired` covers a link past its six hours, one already used and one that
// never existed: the screen offers a new request and nothing else. Otherwise
// the form PUTs Devise's fields with the token as a hidden field; `errors`
// (per field, from the server) re-render the same screen.
export default function ResetPassword({ action, token, email = null, expired = false, errors = {}, links }) {
  const swap = { prompt: "New here?", label: "Create an account", href: links.signUp }

  if (expired) {
    return (
      <AuthLayout panel="reset" swap={swap}>
        <Head title="This link has expired · The Urban Writers" />

        <HeroIcon icon={Clock} />
        <AuthHeading title="This link has expired">
          Reset links work for 6 hours. Request a new one and we’ll send it right away.
        </AuthHeading>

        <div className="flex flex-col gap-2.5">
          <Button asChild className={BUTTON_CLASS}>
            <a href={links.forgot}>Request a new link</a>
          </Button>
          <BackToSignIn href={links.signIn} />
        </div>
      </AuthLayout>
    )
  }

  const failed = Object.keys(errors).length > 0

  return (
    <AuthLayout panel="reset" swap={swap}>
      <Head title="Choose a new password · The Urban Writers" />

      <AuthHeading title="Choose a new password">
        for <b className="font-display">{email}</b>
      </AuthHeading>

      {failed && (
        <AuthAlert tone="bad">
          <b>Couldn’t update your password.</b> Fix the fields below and try again.
        </AuthAlert>
      )}

      <form method="post" action={action} className="flex flex-col gap-4">
        <input type="hidden" name="_method" value="put" />
        <CsrfField />
        <input type="hidden" name="user[reset_password_token]" value={token} />

        <PasswordField
          id="user_password"
          name="user[password]"
          label="New password"
          placeholder="At least 6 characters"
          autoComplete="new-password"
          hint="At least 6 characters"
          error={errors.password}
          minLength={6}
          autoFocus
          required
        />

        <PasswordField
          id="user_password_confirmation"
          name="user[password_confirmation]"
          label="Confirm new password"
          placeholder="Repeat your password"
          autoComplete="new-password"
          error={errors.passwordConfirmation}
          required
        />

        <Button type="submit" className={`mt-[6px] ${BUTTON_CLASS}`}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  )
}
